// Retained, in-memory signal for "the current page is docked-capable for Copilot
// chat" — i.e. chat belongs in the docked side panel rather than a floating panel.
//
// This exists because `copilotDockedPageReadyEvent` is a fire-and-forget
// `CustomEvent`: a subscriber that mounts *after* the dispatch never learns the
// page is docked-capable. On a hard refresh of a PR page the docked page provider
// mounts as part of the page's own hydration, while the embedded chat surface only
// mounts after its payload query and its `React.lazy` chunk resolve — hundreds of
// milliseconds later. It therefore always misses the event and falls back to the
// floating panel. See github/copilot-experiences#12678.
//
// Publishers keep dispatching the DOM events for already-mounted listeners; this
// store additionally retains the state so late mounters can read it synchronously.
//
// Written and read only from client render/effects, so it never runs during server
// rendering and cannot leak state across SSR requests.

let isDockedCapablePage = false
const listeners = new Set<() => void>()

/** Whether the current page has declared itself docked-capable for Copilot chat. */
export function getDockedPageReady(): boolean {
  return isDockedCapablePage
}

/** Mark the current page as docked-capable. Called on mount by docked-capable pages. */
export function publishDockedPageReady(): void {
  if (isDockedCapablePage) return
  isDockedCapablePage = true
  notify()
}

/** Clear the flag when navigating away from a docked-capable page. */
export function clearDockedPageReady(): void {
  if (!isDockedCapablePage) return
  isDockedCapablePage = false
  notify()
}

export function subscribeDockedPageReady(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function notify(): void {
  for (const listener of listeners) {
    listener()
  }
}
